/**
 * Gebuendelter fetch-Client fuer die Mapping-Job- und LLM-Endpunkte (#93).
 *
 * Zweck: ein Aufrufer bekommt bei einem Fehler immer die Backend-Meldung, nie
 * nur einen Statuscode -- und kann per `isNetworkError`/`status` unterscheiden,
 * ob ueberhaupt eine Antwort ankam. Ladezustaende (initial/nachladend/Fehler,
 * AK4) liefert `createRequestState()` in `./requestState.js`.
 */

const DEFAULT_TIMEOUT_MS = 10000;

export class ApiError extends Error {
  constructor(message, { status = null, isNetworkError = false, body = null } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.isNetworkError = isNetworkError;
    this.body = body;
  }
}

function getAuthHeaders() {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * DRF liefert Fehler entweder als {detail|message|error: "..."} oder als
 * Validierungsfehler ohne einheitliches Feld: {feld: ["Meldung", ...]} bzw.
 * {non_field_errors: [...]}, teils auch als reines Array. Ohne diesen Zweig
 * fiel jede Feldvalidierung auf "Bad Request" zurueck (AK3-Verstoss).
 */
function extractDrfMessage(data) {
  if (data == null) {
    return null;
  }
  if (typeof data === 'string' && data) {
    return data;
  }
  if (Array.isArray(data)) {
    const strings = data.filter((entry) => typeof entry === 'string');
    return strings.length ? strings.join('; ') : null;
  }
  if (typeof data !== 'object') {
    return null;
  }
  const direct = data.detail || data.message || data.error;
  if (typeof direct === 'string' && direct) {
    return direct;
  }
  const parts = [];
  for (const [key, value] of Object.entries(data)) {
    const messages = Array.isArray(value)
      ? value.filter((entry) => typeof entry === 'string')
      : (typeof value === 'string' ? [value] : []);
    messages.forEach((message) => {
      parts.push(key === 'non_field_errors' ? message : `${key}: ${message}`);
    });
  }
  return parts.length ? parts.join('; ') : null;
}

async function extractErrorMessage(response) {
  try {
    const data = await response.json();
    const message = extractDrfMessage(data);
    return {
      message: message || response.statusText || `HTTP ${response.status}`,
      body: data,
    };
  } catch {
    return { message: response.statusText || `HTTP ${response.status}`, body: null };
  }
}

function toNetworkError(err, url) {
  return new ApiError(
    err?.name === 'AbortError'
      ? `Zeitueberschreitung bei ${url}`
      : `Netzwerkfehler bei ${url}: ${err.message}`,
    { isNetworkError: true }
  );
}

/**
 * @returns {Promise<any>} geparstes JSON, oder null bei 204 bzw. leerem Body.
 * @throws {ApiError} bei 4xx/5xx (Backend-Meldung in .message/.body) oder
 *   Netzwerkfehler/Timeout (.isNetworkError === true).
 */
export async function apiRequest(url, { method = 'GET', body, timeoutMs = DEFAULT_TIMEOUT_MS } = {}) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  // clearTimeout erst im finally des gesamten Requests (nicht direkt nach
  // fetch()): sonst greift das Zeitlimit nicht mehr, waehrend der Body noch
  // gelesen wird.
  try {
    let response;
    try {
      response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      });
    } catch (err) {
      throw toNetworkError(err, url);
    }

    if (!response.ok) {
      const { message, body: errorBody } = await extractErrorMessage(response);
      throw new ApiError(message, { status: response.status, body: errorBody });
    }

    if (response.status === 204) {
      return null;
    }

    try {
      const text = await response.text();
      return text ? JSON.parse(text) : null;
    } catch (err) {
      if (err instanceof SyntaxError) {
        throw new ApiError(`Antwort von ${url} ist kein gueltiges JSON`, { status: response.status });
      }
      throw toNetworkError(err, url);
    }
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Normalisiert eine Listenantwort (direktes Array oder paginiert
 * {results: [...]}). Eine unbekannte Form wird nicht still zu [] -- das
 * wuerde genau den unbestaetigten Contract aus #57 verdecken.
 */
export function normalizeListResponse(value, { source = '' } = {}) {
  if (Array.isArray(value)) {
    return value;
  }
  if (Array.isArray(value?.results)) {
    return value.results;
  }
  throw new ApiError(
    `Unerwartete Listenantwort${source ? ` von ${source}` : ''}: weder Array noch {results: [...]}`,
    { body: value }
  );
}
